/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IAction, IActionInput, IObservableAction } from '@msdyn365-commerce/core-internal';
import { ActionRegistrar } from './action-registrar';

export type dataActionOptions<TOut> = {
    action: IAction<TOut>;
    isBatched?: boolean;
    id?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    input?: (...args: any[]) => IActionInput | IActionInput[];
};

type IActionWrap<TOut> = IAction<TOut> | IObservableAction<TOut>;

/**
 * Wraps a data action with the associated action wrap.
 * @param options
 * @param actionWrap
 */
export const wrapDataAction = <TOut>(options: dataActionOptions<TOut>, actionWrap: IActionWrap<TOut>): IActionWrap<TOut> => {
    if (!options) {
        throw new Error('wrapDataAction called with invalid options. Options should have atleast action defined');
    }

    if (!options.action) {
        throw new Error(
            `Received invalid actions when attempting to parse options. Input name: ${options.input ? options.input.name : 'undefined'}`
        );
    }

    let inputFunc = options.input!;
    let id = options.id;
    let isBatched = options.isBatched!;
    options.action.prototype = {
        id,
        isBatched,
        isAction: true
    };

    actionWrap.prototype = {
        id,
        isAction: false,
        _action: options.action,
        set RegistrationId(registrationId: string) {
            options.action.prototype.RegistrationId = registrationId;
        },
        get RegistrationId(): string {
            return options.action.prototype.RegistrationId || '';
        },
        set inputFunc(func: Function) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (<any>inputFunc) = func;
        },
        get inputFunc(): Function {
            return inputFunc;
        },
        get isBatched(): boolean {
            return isBatched;
        },
        set isBatched(_isBatched: boolean) {
            isBatched = _isBatched;
        },
        get Action(): IAction<TOut> {
            return options.action;
        },
        set Action(newAction: IAction<TOut>) {
            // There are no getter/setters on the action function prototype so this behaves as expected.
            inputFunc = newAction.prototype.input || inputFunc;
            id = newAction.prototype.id || id;
            isBatched = newAction.prototype.isBatched || isBatched;
            newAction.prototype = { ...newAction.prototype, ...options.action.prototype };
            options.action = newAction;
        },
        setProperty: (propertyName: string, value: unknown) => {
            options.action.prototype[propertyName] = value;
            actionWrap.prototype[propertyName] = options.action.prototype[propertyName];
        }
    };
    if (actionWrap.prototype.id) {
        ActionRegistrar.registerAction(actionWrap.prototype.id, actionWrap);
    }

    return actionWrap;
};
